import { JobsScrapeStatus } from "@/enum/scrape-enum";

import { LiPost } from "../../LiPost/schema/liPost";
import { List } from "../schema/list";

/** The Maps-only part of a source row (PREC-4994): state, search and the stored location. */
export interface ListSourceMapsDetails {
	status: JobsScrapeStatus;
	query: string;
	city: string;
	country: string;
	/** Retained leads in this list attributed to this map; absent when the API did not supply it. */
	leadCount?: number;
}

/** A source feeding a list, normalized across the per-type arrays for display purposes. */
export interface ListSourceItem {
	/** Unique across source types — different source tables share numeric ids. */
	key: string;
	id: number;
	name: string;
	/** Present only for a Google Maps source. */
	maps?: ListSourceMapsDetails;
}

const sourceKey = (type: string, id: number) => `${type}-${id}`;

/** Normalizes a source that carries a user-set `name` field. */
const toNamedSourceItem = (
	type: string,
	{ id, name }: { id: number; name?: string | null }
): ListSourceItem => ({
	key: sourceKey(type, id),
	id,
	name: name ?? "",
});

/**
 * LI_POST sources have no user-set name (the CreateLiPost form never sets one), so `name` is empty
 * and the row would render blank. Mirror the fallback chain AddedSourceList already uses for the
 * source "value" column so the dropdown shows something meaningful for each LI_POST type
 * (BY_TOPIC → query, BY_URL → postUrl, BY_PROFILE → member/organization title).
 */
const getLiPostSourceLabel = (liPost: LiPost): string =>
	liPost.name ||
	liPost.query ||
	liPost.postUrl ||
	liPost.fromMember?.title ||
	liPost.fromOrganization?.title ||
	"";

const toLiPostSourceItem = (liPost: LiPost): ListSourceItem => ({
	key: sourceKey("liPosts", liPost.id),
	id: liPost.id,
	name: getLiPostSourceLabel(liPost),
});

const toMapsSourceItem = (map: NonNullable<List["maps"]>[number]): ListSourceItem => ({
	...toNamedSourceItem("maps", map),
	maps: {
		status: map.status,
		query: map.query,
		city: map.city,
		country: map.country,
		leadCount: map.leadCount,
	},
});

/**
 * Flattens the full list's per-type source arrays (the same set AddedSourceList renders) into one
 * display list for the Sources header dropdown. Requires the *full* list — the lean single-list
 * payload drops these arrays.
 */
export const getListSources = (list: List): ListSourceItem[] => {
	const trackerFiles = list.trackers?.flatMap((tracker) => tracker.trackerFiles || []) || [];
	const named = (type: string) => (source: { id: number; name?: string | null }) =>
		toNamedSourceItem(type, source);

	return [
		...(list.maps || []).map(toMapsSourceItem),
		...(list.jobs || []).map(named("jobs")),
		...(list.csvEnrichments || []).map(named("csvEnrichments")),
		...(list.liEvents || []).map(named("liEvents")),
		...(list.liCompanyActivity || []).map(named("liCompanyActivity")),
		...(list.liPosts || []).map(toLiPostSourceItem),
		...(list.liProfileVisits || []).map(named("liProfileVisits")),
		...(list.liProfile || []).map(named("liProfile")),
		...(list.liPeople || []).map(named("liPeople")),
		...(list.liSn || []).map(named("liSn")),
		...(list.fundingMa || []).map(named("fundingMa")),
		...(list.swarms || []).map(named("swarms")),
		...(list.parallelAi || []).map(named("parallelAi")),
		...(list.companyEnriches || []).map(named("companyEnriches")),
		...trackerFiles.map(named("trackerFiles")),
	];
};
